// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../validation/runtime-checks/index.js';
import { EANContinueCondition } from './EANContinueCondition.js';
import Latex from '../Latex.js';
import { productColor } from '../designSystem.js';
import { SubHeading } from '../Heading.js';
import { LatexTextAtom } from '../LatexTextAtom.js';

export function EANLaunchSection(props: {|
  timeEstimate: number,
  // NOTE: Encoding all the tuple permutations due to the optional elements
  // gets excessive, so we'll use the less accurate generic ChildrenArray type
  children: ChildrenArray<
    | Element<typeof EANLaunchDescription>
    | Element<typeof EANUnderstandingCheck>
    | Element<typeof EANContextCheck>
    | Element<typeof EANGroupingCheck>
    | Element<typeof EANContinueCondition>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, EANLaunchSection);
  if (failedValidation) return null;
  return (
    <div style={{ color: productColor.shuttleGrey }}>
      <div style={{ marginTop: 40 }}>
        <SubHeading>Launch</SubHeading>
        <div style={{ color: productColor.shuttleGrey }}>
          {props.timeEstimate} mins
        </div>
      </div>
      <div style={{ marginTop: 16 }}>{props.children}</div>
    </div>
  );
}
EANLaunchSection.spec = {
  timeEstimate: t.number,
  children: t.exactOrderChildrenArray(
    t.elementType(EANLaunchDescription),
    t.optional(t.elementType(EANUnderstandingCheck)),
    t.optional(t.elementType(EANContextCheck)),
    t.elementType(EANGroupingCheck),
    t.optional(t.elementType(EANContinueCondition)),
  ),
};

export function EANLaunchDescription(props: {|
  children: ChildrenArray<
    string | Element<typeof Latex> | Element<typeof LatexTextAtom>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, EANLaunchDescription);
  if (failedValidation) return null;
  return <div>{props.children}</div>;
}
EANLaunchDescription.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
    ),
  ),
};

export function EANUnderstandingCheck(props: {|
  children: ChildrenArray<
    string | Element<typeof Latex> | Element<typeof LatexTextAtom>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, EANUnderstandingCheck);
  if (failedValidation) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <span style={{ fontWeight: 600 }}>Important mathematical concepts:</span>{' '}
      {props.children}
    </div>
  );
}
EANUnderstandingCheck.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
    ),
  ),
};

export function EANContextCheck(props: {|
  children: ChildrenArray<
    string | Element<typeof Latex> | Element<typeof LatexTextAtom>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, EANContextCheck);
  if (failedValidation) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <span style={{ fontWeight: 600 }}>Important contextual information:</span>{' '}
      {props.children}
    </div>
  );
}
EANContextCheck.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
    ),
  ),
};

export function EANGroupingCheck(props: {|
  children: ChildrenArray<
    string | Element<typeof Latex> | Element<typeof LatexTextAtom>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, EANGroupingCheck);
  if (failedValidation) return null;
  return (
    <div style={{ marginTop: 8 }}>Suggested grouping: {props.children}</div>
  );
}
EANGroupingCheck.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
    ),
  ),
};
